/*
 *   File : employee.js
 *   Author : https://evoqins.com
 *   Description : Employee listing page
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'bootstrap';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

// Custom components 
import { DataTableComponent } from '../../../Component/DataTable';
import { CustomDatePicker, CustomSearchInput, CustomSelectBox } from '../../../Component/FormElements';
import { APIService } from '../../../Service';
import { Loader, OverLay } from '../../../Component/Others';
import { EmptyScreen } from '../../../Component/EmptyComponent';
import { DownloadButton, PrimaryButton, TextButton } from '../../../Component/Buttons';
import { ManageAssignModal } from '../../../Component/Modal';
import { Icon } from '../../../Component/Icon';
import { StatisticCard } from '../../../Component/Cards';

const ITEMS_PER_PAGE = 20;

const SALES_SPECIALIST_SORT_FIELDS = [
    'user_id',
    'name',
    'store',
    'country_admin',
    'joined_on',
    'volume',
    'reward_points',
    'status',
];

const SalesSpecialist = () => {

    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);

    const navigate = useNavigate();
    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);

    const [selected, setSelected] = useState(null);
    const [toggleFilter, setToggleFilter] = useState(false);

    const [registeredUserData, setRegisteredUserData] = useState({});
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const [assignModal, setAssignModal] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    const [countryAdmins, setCountryAdmins] = useState([]);
    const [adminDataLoading, setAdminDataLoading] = useState(true);
    const [cityOptions, setCityOptions] = useState([]);
    const [cityOptionsLoading, setCityOptionsLoading] = useState(true);
    const [selectedStore, setSelectedStore] = useState(null);

    const [searchValue, setSearchValue] = useState("");

    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedDayError, setSelectedDayError] = useState("");
    const [dateToggle, setDateToggle] = useState(false);

    const [clearFilter, setClearFilter] = useState(false);

    const [downloadLoader, setDownloadLoader] = useState(false);

    const [selectedStatus, setSelectedStatus] = useState(null);

    const [filterData, setFilterData] = useState({});

    const [summaryData, setSummaryData] = useState({});
    const [summaryDataLoading, setSummaryDataLoading] = useState(true);

    const [endDate, setEndDate] = useState(null);
    const [endDateToggle, setEndDateToggle] = useState(false);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });

        _getCity();
        _filterData();

        _getSummaryData();
    }, []);

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }
        _getRegisteredUserData();
    }, [currentPage, clearFilter, searchValue]);

    useEffect(() => {
        if (assignModal === true) {
            const assign_modal = new Modal(document.getElementById('manage-assign-modal'));
            assign_modal.show();
        }
    }, [assignModal]);


    // handle navigate detail page 
    const _handleNavigate = (id) => {
        navigate("detail", {
            state: {
                customer_id: id,
                tabIndex: 1
            }
        });
    };

    // handle page change 
    const _handlePageChange = (page_num) => {
        // Ignore RDT's automatic page reset while we handle sort ourselves
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);
        setSelectedIds([]);

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;

        if (!sortBy || !SALES_SPECIALIST_SORT_FIELDS.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setLoading(true);
        setSelectedIds([]);

        // Only skip the list useEffect when page will actually change.
        // If already on page 1, useEffect won't run and a stuck skip flag
        // would block the next pagination API call (loader stuck).
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        _getRegisteredUserData(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    // handle select employee ids
    const _handleSelectIds = (id) => {
        setSelectedIds((prev) => {
            if (prev.includes(id)) {
                return prev.filter((selectedId) => selectedId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    // handle assign modal 
    const _handleAssign = () => {
        setAssignModal(true);

        _getCountryAdminList(); //API admin list
    };

    // handle select all rows
    const _handleSelectAll = () => {
        if (selectedIds.length === registeredUserData.data.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(registeredUserData.data.map((row) => row.customer_id));
        }
    };

    const _handleClearAll = () => {
        setSelected(filterData.country_admin[0]);
        setSelectedStore(filterData.store[0]);
        setSelectedStatus(filterData.status[0])
        setSelectedDay(null);
        setEndDate(null);

        setClearFilter(true);
        setLoading(true);
        setCurrentPage(1);
        setSortField(null);
        setSortDirection(null);
    };


    // API - Get sales specialist data
    const _getRegisteredUserData = (page_no = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        let is_post = true;
        let url = 'CA/sales-specialist/list';
        let data = {
            "page_num": page_no,
            "limit": ITEMS_PER_PAGE,
            "query": searchValue,
            "country_admin_id": selected?.value || null,
            "store_id": selectedStore?.value || null,
            "status": selectedStatus?.value || null,
            "start_date": selectedDay !== null ? format(selectedDay, 'yyyy-MM-dd') : null,
            "end_date": endDate !== null ? format(endDate, 'yyyy-MM-dd') : null,
            "sort_by": sort_by ? [sort_by] : null,
            "order": sort_order ? [sort_order] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setRegisteredUserData(response.data);
            } else {
                setRegisteredUserData({});
            }
            setLoading(false);
            setClearFilter(false);
        });
    };

    // API - Get country admin list
    const _getCountryAdminList = () => {
        let is_post = false;
        let url = 'general/country-admin';
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setCountryAdmins(response.data);
            } else { }
            setAdminDataLoading(false);
        });
    };

    // API - City list
    const _getCity = () => {
        let city_filter_options = [];

        let is_post = false;
        let url = "general/city";
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {

                city_filter_options.push({
                    value: null,
                    label: "All"
                });

                response.data.map((city) => (
                    city_filter_options.push({
                        value: city.value,
                        label: city.label
                    })
                ));

                setCityOptions(city_filter_options);

            } else { }
            setCityOptionsLoading(false);
        });
    };


    // API - Download data
    const _download = () => {
        setDownloadLoader(true);

        let is_post = true;
        let url = "CA/sales-specialist/download";
        let data = {
            "country_admin_id": selected?.value || null,
            "store_id": selectedStore?.value || null,
            "status": selectedStatus?.value || null,
            "start_date": selectedDay !== null ? format(selectedDay, 'yyyy-MM-dd') : null,
            "end_date": endDate !== null ? format(endDate, 'yyyy-MM-dd') : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const url = response.file_url;
                const link = document.createElement('a');
                link.href = url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };


    // API - Filter data
    const _filterData = () => {
        let is_post = false;
        let url = "CA/sales-specialist/filter-data";
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {

                const updated_filter_data = Object.keys(response.data).reduce(
                    (acc, key) => {
                        const list = response.data[key].map(item => ({
                            value: item.id,
                            label: item.name
                        }));

                        acc[key] = key === "status"
                            ? list
                            : [{ value: null, label: "All" }, ...list];

                        return acc;
                    },
                    {}
                );

                setFilterData(updated_filter_data);

                setSelected(updated_filter_data.country_admin[0]);
                setSelectedStore(updated_filter_data.store[0]);
                setSelectedStatus(updated_filter_data.status[0]);
            } else {
                setFilterData({})
            }
        });
    };


    // API - Summary data
    const _getSummaryData = () => {
        let is_post = false;
        let url = "CA/sales-specialist/get";
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setSummaryData(response.data);
            } else {
                setSummaryData({});
            }
            setSummaryDataLoading(false);
        });
    };


    // registered members table 
    const TABLE_COLUMNS = [
        {
            name: (
                <Icon icon={selectedIds.length === registeredUserData?.data?.length ? "checkbox-active" : "checkbox"}
                    className='cursor-pointer'
                    size="16px"
                    onPress={_handleSelectAll} />
            ),
            cell: (row) => <div >
                <Icon icon={selectedIds.includes(row.customer_id) ? "checkbox-active" : "checkbox"}
                    className='cursor-pointer'
                    size="16px"
                    onPress={() => _handleSelectIds(row.customer_id)} />
            </div>,
            width: "40px"
        },
        {
            name: 'User ID',
            id: 'user_id',
            sortField: 'user_id',
            selector: row => row.customer_id || "-",
            cell: row => <>{row.user_id || "-"}</>,
            width: "120px",
            sortable: true,
        },
        {
            name: 'Name',
            id: 'name',
            sortField: 'name',
            selector: row => row.customer_name || "-",
            cell: row => <>{row.customer_name}</>,
            width: "280px",
            sortable: true,
        },
        {
            name: 'Store',
            id: 'store',
            sortField: 'store',
            selector: row => row.store_name || "-",
            cell: row => <>{row.store_name}</>,
            width: "300px",
            sortable: true,
        },
        {
            name: 'Country admin',
            id: 'country_admin',
            sortField: 'country_admin',
            selector: row => row.country_admin ? row.country_admin : '-',
            width: "180px",
            sortable: true,
        },
        {
            name: 'Joined on',
            id: 'joined_on',
            sortField: 'joined_on',
            selector: row => row.joined_on,
            cell: row => <>{row.joined_on}</>,
            width: "120px",
            format: row => row.redeemed_on,
            sortable: true,
        },
        // {
        //     name: 'Email',
        //     selector: row => row.email === null ? "-" : row.email,
        //     width: "200px"
        // },
        // {
        //     name: 'Phone no.',
        //     selector: row => row.contact_no,
        //     width: "160px"
        // },

        {
            name: PROFILE_DATA.country === "Egypt" ? 'Volume (Units)' : 'Volume (Ltr)',
            id: 'volume',
            sortField: 'volume',
            selector: row => row.total_sales,
            width: "140px",
            sortable: true,
        },
        {
            name: 'Reward points',
            id: 'reward_points',
            sortField: 'reward_points',
            selector: row => row.reward,
            width: "140px",
            sortable: true,
        },
        // {
        //     name: 'Revenue generated',
        //     selector: row => row.revenue,
        //     width: "180px"
        // },

        {
            name: 'Status',
            id: 'status',
            sortField: 'status',
            selector: row => row.active_status || "-",
            cell: row =>
                <span className={`${row.active_status === "Active" ? "e-text-philippine-green " : "e-text-boston-university-re"} e-font-weight-600`}>
                    {row.active_status ? row.active_status : "-"} 
                </span>,
            width: "100px",
            sortable: true,
        },


    ];

    return (
        <section className='pb-5 '>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr'>
                <h1 className='mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-raisin-black e-font-weight-600'>
                    Sales Specialists
                </h1>
            </div>
            <div className='margin-neg-66px-top padding-52px-lr '>

                {/* <div className='row g-3 row-gap-3 row-cols-lg-4 row-cols-md-4 row-cols-sm-2 row-cols-2'>
                    <div className='col m-0'>
                        <StatisticCard
                            loader={summaryDataLoading}
                            icon={require("../../../Assets/Image/CountryAdmin/total-registered.svg").default}
                            quantity={summaryData.total_registered}
                            title={"Total registered"}
                            className="e-border-radius-8 p-3" />
                    </div>
                    <div className='col m-0 '>
                        <StatisticCard
                            loader={summaryDataLoading}
                            icon={require("../../../Assets/Image/CountryAdmin/active-users.svg").default}
                            quantity={summaryData.active_users}
                            title={"Active users"}
                            className="e-border-radius-8 p-3" />
                    </div>
                    <div className='col m-0 '>
                        <StatisticCard
                            loader={summaryDataLoading}
                            icon={require("../../../Assets/Image/CountryAdmin/points.svg").default}
                            quantity={summaryData.points}
                            title={"Points"}
                            className="e-border-radius-8 p-3" />
                    </div>
                    <div className='col m-0 '>
                        <StatisticCard
                            loader={summaryDataLoading}
                            icon={require("../../../Assets/Image/CountryAdmin/redemptions.svg").default}
                            quantity={summaryData.redemptions}
                            title={"Redemptions"}
                            className="e-border-radius-8 p-3" />
                    </div>
                </div> */}

                <div className='e-bg-white e-admin-box-shadow py-4 e-border-radius-16 e-border-1px-platinum-dark'>
                    <div className='d-flex justify-content-space-between px-4'>
                        <h4 className='mb-0 e-text-rich-black e-alt-font-noto-sans e-font-18 e-font-weight-500 line-height-28px'>
                            All registered members
                        </h4>
                        <div className='d-flex align-items-center gap-4'>

                            <PrimaryButton name="Assign"
                                className={`${selectedIds.length > 0 ? "" : "e-disable"} py-2 e-border-radius-8`}
                                onPress={_handleAssign} />

                            <DownloadButton loader={downloadLoader}
                                disabled={loading || Object.keys(registeredUserData).length === 0 || registeredUserData?.data?.length === 0}
                                onClick={_download} />

                            <CustomSearchInput placeholder={"Search by name, id & store"}
                                type="text"
                                require
                                value={searchValue}
                                className="w-100 padding-8px-tb py-2 "
                                labelStyle="e-font-weight-500"
                                iconClass="top-10px"
                                handleChange={(e) => setSearchValue(e)}
                                handleClear={() => { setSearchValue("") }} />

                            <img src={toggleFilter ? require("../../../Assets/Image/filter-icon-white.svg").default : require("../../../Assets/Image/filter-icon.svg").default}
                                draggable={false}
                                // height={38}
                                // width={38}
                                className={`${cityOptionsLoading ? 'e-disable' : ''} ${toggleFilter ? `e-bg-indigo` : ``} transition e-border-1px-anti-flash-white e-border-radius-8 p-2 cursor-pointer`}
                                alt='filter'
                                onClick={() => setToggleFilter(!toggleFilter)} />
                        </div>
                    </div>



                    {/* filter */}
                    {toggleFilter &&
                        <div className='e-bg-anti-flash-white-dark py-3 px-4 mt-3'>
                            <div className='row'>
                                <div className='col-md-3 col-sm-4 col-6'>
                                    <CustomSelectBox label="Country admin"
                                        value={selected}
                                        options={filterData.country_admin}
                                        type={2}
                                        optional={true}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        isSearchable={false}
                                        selectChange={(value) => setSelected(value)}
                                    />
                                </div>
                                {/* City filter */}
                                <div className='col-md-3 col-sm-4 col-6'>
                                    <CustomSelectBox label="Store"
                                        optional={true}
                                        value={selectedStore}
                                        options={filterData.store}
                                        type={2}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        isSearchable={false}
                                        selectChange={(value) => setSelectedStore(value)}
                                    />
                                </div>


                                <div className='col-md-3 col-sm-4 col-6'>
                                    <p className='mb-0 e-text-dark-black e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-20px d-flex'>
                                        Date of joining
                                    </p>
                                    {/* Pick date  */}
                                    <div className='pt-1 row'>
                                        <div className='position-relative '>
                                            <div className={`${dateToggle ? 'e-border-1px-indigo' : 'e-border-1px-platinum-light'} e-display-flex gap-0-4px justify-content-between e-border-radius-4 p-2 cursor-pointer e-bg-white`}
                                                onClick={() => setDateToggle(!dateToggle)}>

                                                {selectedDay !== null ?
                                                    <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-16px'>
                                                        {format(selectedDay, 'PP')}
                                                    </p>
                                                    :
                                                    <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-16px'>
                                                        From
                                                    </p>
                                                }
                                                <div className='d-flex' onClick={(e) => e.stopPropagation()}>
                                                    {selectedDay === null ?
                                                        <img src={require("../../../Assets/Image/CountryAdmin/Notification/calendar-icon.svg").default}
                                                            draggable={false}
                                                            width={16}
                                                            height={16}
                                                            alt='calender-icon' />

                                                        :
                                                        < Icon icon="close-icon"
                                                            className='cursor-pointer d-flex'
                                                            size={16}
                                                            onPress={() => {
                                                                setSelectedDay(null);
                                                                setEndDate(null);
                                                            }} />
                                                    }
                                                </div>
                                            </div>

                                            {/* error message */}
                                            <p className='mb-0 e-error-message e-alt-font-noto-sans e-font-12 line-height-12px padding-2-top'>
                                                {selectedDayError}
                                            </p>


                                        </div>
                                    </div>

                                    {/* date picker */}
                                    {dateToggle &&
                                        <>
                                            <OverLay className="e-bg-overlay " isOpen={dateToggle} onClose={() => setDateToggle(!dateToggle)} />
                                            <CustomDatePicker selected={selectedDay}
                                                className='e-day-picker w-358px position-absolute e-bg-white e-border-radius-16 z-index-99'
                                                disableFuture={true}
                                                onSelect={(day) => {
                                                    setSelectedDay(day);
                                                    setDateToggle(false);
                                                    setEndDate(null);
                                                }} />
                                        </>
                                    }
                                </div>

                                <div className='col-md-3 col-sm-4 col-6 mt-auto position-relative'>

                                    <div className='pt-1 row'>
                                        <div className='position-relative '>
                                            <div className={`${endDateToggle ? 'e-border-1px-indigo' : 'e-border-1px-platinum-light'} e-display-flex gap-0-4px justify-content-between e-border-radius-4 p-2 cursor-pointer e-bg-white`}
                                                onClick={() => setEndDateToggle(!endDateToggle)}>

                                                {endDate !== null ?
                                                    <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-16px'>
                                                        {format(endDate, 'PP')}
                                                    </p>
                                                    :
                                                    <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-16px'>
                                                        To
                                                    </p>
                                                }
                                                <div className='d-flex' onClick={(e) => e.stopPropagation()}>
                                                    {endDate === null ?
                                                        <img src={require("../../../Assets/Image/CountryAdmin/Notification/calendar-icon.svg").default}
                                                            draggable={false}
                                                            width={16}
                                                            height={16}
                                                            alt='calender-icon' />

                                                        :
                                                        < Icon icon="close-icon"
                                                            className='cursor-pointer d-flex'
                                                            size={16}
                                                            onPress={() => setEndDate(null)} />
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* date picker */}
                                    {endDateToggle &&
                                        <>
                                            <OverLay className="e-bg-overlay " isOpen={endDateToggle} onClose={() => setEndDateToggle(!endDateToggle)} />
                                            <CustomDatePicker selected={endDate}
                                                className='e-day-picker w-358px end-0 position-absolute e-bg-white e-border-radius-16 z-index-99'
                                                disableFuture={true}
                                                minDate={selectedDay}
                                                onSelect={(day) => {
                                                    setEndDate(day);
                                                    setEndDateToggle(false);
                                                }} />
                                        </>
                                    }
                                </div>
                            </div>


                            <div className='row pt-3'>
                                <div className='col-md-3 col-sm-4 col-6'>
                                    <CustomSelectBox label="Status"
                                        optional={true}
                                        value={selectedStatus}
                                        options={filterData.status}
                                        type={2}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        isSearchable={false}
                                        selectChange={(value) => setSelectedStatus(value)}
                                    />
                                </div>

                                <div className='col-md-9 col-sm-8 col-6 mt-auto'>
                                    <div className='d-flex gap-3 align-items-center justify-content-end'>
                                        <TextButton name="Clear all"
                                            onPress={_handleClearAll} />
                                        <PrimaryButton name="Filter now"
                                            className="px-3 py-1 e-border-radius-8"
                                            onPress={() => {
                                                _getRegisteredUserData(1)
                                                setLoading(true);
                                            }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    {loading ?
                        // loader 
                        <Loader />
                        :
                        <>
                            {/* Registered members list table */}
                            {Object.keys(registeredUserData).length !== 0 && registeredUserData?.data?.length !== 0 ?
                                <div className='e-members-table'>
                                    <DataTableComponent
                                        key={`sales-specialist-table-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                        columns={TABLE_COLUMNS}
                                        data={registeredUserData.data}
                                        pagination={true}
                                        paginationServer={true}
                                        paginationTotalRows={registeredUserData.total_count}
                                        paginationPerPage={ITEMS_PER_PAGE}
                                        // selectableRows={true}
                                        paginationDefaultPage={currentPage}
                                        paginationResetDefaultPage={false}
                                        sortServer={true}
                                        defaultSortFieldId={sortField}
                                        defaultSortAsc={sortDirection !== 'desc'}
                                        onSort={_handleSort}
                                        onChangePage={_handlePageChange}
                                        rowClick={(row) => _handleNavigate(row.customer_id)} />
                                </div>
                                :
                                // empty screen
                                <div className='h-100vh pt-5 mt-5'>
                                    <EmptyScreen message="No sales specialist found" />
                                </div>
                            }
                        </>
                    }
                </div>
            </div>

            {/* assign modal */}
            {assignModal === true &&
                <ManageAssignModal options={countryAdmins}
                    loading={adminDataLoading}
                    customerIds={selectedIds}
                    handleClose={() => setAssignModal(false)}
                    handleSuccess={() => {
                        setAssignModal(false);
                        _getRegisteredUserData();
                        setSelectedIds([]);
                    }} />
            }
        </section>
    )
}

export default SalesSpecialist;